import React, {useEffect, useRef, useState} from 'react';
import {Link, useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api, API_BASE, getCookie} from '../../api';

const DOC_TYPES = [
  {type: 'docx', label: 'Document', icon: 'W'},
  {type: 'xlsx', label: 'Spreadsheet', icon: 'X'},
  {type: 'pptx', label: 'Presentation', icon: 'P'},
];

const FILE_ICONS = {
  'application/pdf': {color: 'text-red-600', bg: 'bg-red-50', label: 'PDF'},
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {color: 'text-blue-600', bg: 'bg-blue-50', label: 'DOCX'},
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {color: 'text-green-600', bg: 'bg-green-50', label: 'XLSX'},
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {color: 'text-orange-600', bg: 'bg-orange-50', label: 'PPTX'},
  'text/plain': {color: 'text-gray-600', bg: 'bg-gray-50', label: 'TXT'},
  'text/csv': {color: 'text-green-600', bg: 'bg-green-50', label: 'CSV'},
};

function FileIcon({contentType}) {
  const isImage = contentType?.startsWith('image/');
  const match = FILE_ICONS[contentType];
  const color = match?.color || (isImage ? 'text-purple-600' : 'text-gray-500');
  const bg = match?.bg || (isImage ? 'bg-purple-50' : 'bg-gray-50');
  const label = match?.label || (isImage ? contentType.split('/')[1].toUpperCase() : contentType?.split('/').pop()?.toUpperCase() || '?');

  const icon = isImage ? (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    </svg>
  );

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${bg} ${color} text-xs font-medium`}>
      {icon}{label}
    </span>
  );
}

const FolderIcon = () => (
  <svg className="w-5 h-5 text-cyan-500" fill="currentColor" viewBox="0 0 20 20">
    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
  </svg>
);

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [createType, setCreateType] = useState(null);
  const [createName, setCreateName] = useState('');
  const [folderModal, setFolderModal] = useState(null); // 'new' | {id, name} for rename
  const [folderName, setFolderName] = useState('');
  const [deleteFolder, setDeleteFolder] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  const [successBanner, setSuccessBanner] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const fileRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const folderId = searchParams.get('folderId') || null;

  useEffect(() => {
    if (location.state?.successBanner) {
      setSuccessBanner(location.state.successBanner);
      window.history.replaceState({}, '');
      setTimeout(() => setSuccessBanner(null), 3000);
    }
  }, [location.state]);

  const load = async () => {
    const [foldersRes, docsRes] = await Promise.all([
      api('/document-folders'),
      api(`/documents${folderId ? `?folderId=${folderId}` : ''}`),
    ]);
    const allFolders = foldersRes.ok ? await foldersRes.json() : [];
    // Filter to show only children of current folder
    setFolders(allFolders.filter(f => folderId ? f.parentId === folderId : !f.parentId));
    setDocuments(docsRes.ok ? await docsRes.json() : []);
    // Build breadcrumbs
    if (!folderId) { setBreadcrumbs([]); return; }
    const map = Object.fromEntries(allFolders.map(f => [f.id, f]));
    const crumbs = [];
    let current = map[folderId];
    while (current) {
      crumbs.unshift(current);
      current = current.parentId ? map[current.parentId] : null;
    }
    setBreadcrumbs(crumbs);
  };

  useEffect(() => { load(); }, [folderId]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) { load(); return; }
    setSearching(true);
    const res = await api(`/documents?q=${encodeURIComponent(searchQuery.trim())}`);
    setDocuments(res.ok ? await res.json() : []);
    setFolders([]);
    setSearching(false);
  };

  const clearSearch = () => {
    setSearchQuery('');
    load();
  };

  useEffect(() => {
    if (!openMenu) return;
    const close = () => setOpenMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openMenu]);

  const navigateToFolder = (id) => {
    if (id) setSearchParams({folderId: id});
    else setSearchParams({});
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    if (folderId) formData.append('folderId', folderId);
    const jwtToken = getCookie('jwtToken');
    const tenantId = getCookie('tenantId');
    const headers = {};
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    await fetch(`${API_BASE}/documents`, { method: 'POST', headers, body: formData });
    fileRef.current.value = '';
    setUploading(false);
    load();
  };

  const openCreateModal = (type) => {
    setShowNew(false);
    setCreateType(type);
    setCreateName('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createName.trim()) return;
    const body = { fileName: createName.trim(), type: createType };
    if (folderId) body.folderId = folderId;
    const res = await api('/documents/create', { method: 'POST', body: JSON.stringify(body) });
    setCreateType(null);
    if (res.ok) {
      const doc = await res.json();
      navigate(`/documents/${doc.id}/edit`);
    }
  };

  const openFolderModal = (existing) => {
    if (existing) {
      setFolderModal({id: existing.id, name: existing.name});
      setFolderName(existing.name);
    } else {
      setFolderModal('new');
      setFolderName('');
    }
  };

  const handleFolderSubmit = async (e) => {
    e.preventDefault();
    if (!folderName.trim()) return;
    if (folderModal === 'new') {
      const body = { name: folderName.trim() };
      if (folderId) body.parentId = folderId;
      await api('/document-folders', { method: 'POST', body: JSON.stringify(body) });
    } else {
      await api(`/document-folders/${folderModal.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: folderName.trim(), parentId: folderId }),
      });
    }
    setFolderModal(null);
    load();
  };

  const handleDeleteFolder = async () => {
    if (!deleteFolder) return;
    await api(`/document-folders/${deleteFolder.id}`, { method: 'DELETE' });
    setDeleteFolder(null);
    load();
  };

  const onDragStart = (e, type, id) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({type, id}));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverId(targetId);
  };

  const onDragLeave = () => setDragOverId(null);

  const onDrop = (e, targetFolderId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverId(null);
    try {
      const {type, id} = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (type === 'folder' && id === targetFolderId) return;
      const itemName = type === 'doc'
        ? documents.find(d => d.id === id)?.fileName
        : folders.find(f => f.id === id)?.name;
      const targetName = targetFolderId
        ? [...folders, ...breadcrumbs].find(f => f.id === targetFolderId)?.name
        : 'Root';
      setPendingMove({type, id, targetFolderId, itemName: itemName || 'item', targetName: targetName || 'Root'});
    } catch {}
  };

  const confirmMove = async () => {
    if (!pendingMove) return;
    const {type, id, targetFolderId, itemName, targetName} = pendingMove;
    setPendingMove(null);
    if (type === 'doc') {
      await api(`/documents/${id}`, { method: 'PUT', body: JSON.stringify({folderId: targetFolderId || null}) });
    } else {
      await api(`/document-folders/${id}`, { method: 'PUT', body: JSON.stringify({name: folders.find(f => f.id === id)?.name || 'Folder', parentId: targetFolderId || null}) });
    }
    setSuccessBanner(`Moved "${itemName}" to ${targetName}`);
    setTimeout(() => setSuccessBanner(null), 3000);
    load();
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Layout>
      {successBanner && (
        <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 text-green-800 rounded-lg flex items-center justify-between">
          <span>{successBanner}</span>
          <button onClick={() => setSuccessBanner(null)} className="text-green-600 hover:text-green-800 ml-4">✕</button>
        </div>
      )}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Documents</h2>
          {breadcrumbs.length > 0 && (
            <nav className="flex items-center gap-1 mt-1 text-sm">
              <button onClick={() => navigateToFolder(null)}
                onDragOver={e => onDragOver(e, 'root')} onDragLeave={onDragLeave} onDrop={e => onDrop(e, null)}
                className={`text-cyan-600 hover:text-cyan-800 px-1 rounded ${dragOverId === 'root' ? 'bg-cyan-100 ring-2 ring-cyan-400' : ''}`}>Root</button>
              {breadcrumbs.map((crumb, i) => (
                <span key={crumb.id} className="flex items-center gap-1">
                  <span className="text-gray-400">/</span>
                  {i < breadcrumbs.length - 1 ? (
                    <button onClick={() => navigateToFolder(crumb.id)}
                      onDragOver={e => onDragOver(e, crumb.id)} onDragLeave={onDragLeave} onDrop={e => onDrop(e, crumb.id)}
                      className={`text-cyan-600 hover:text-cyan-800 px-1 rounded ${dragOverId === crumb.id ? 'bg-cyan-100 ring-2 ring-cyan-400' : ''}`}>{crumb.name}</button>
                  ) : (
                    <span className="text-gray-700 font-medium">{crumb.name}</span>
                  )}
                </span>
              ))}
            </nav>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={() => openFolderModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            New Folder
          </button>
          <div className="relative">
            <button onClick={() => setShowNew(!showNew)} className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">
              New Document ▾
            </button>
            {showNew && (
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border z-10">
                {DOC_TYPES.map(dt => (
                  <button key={dt.type} onClick={() => openCreateModal(dt.type)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3">
                    <span className="w-8 h-8 bg-cyan-100 text-cyan-700 rounded flex items-center justify-center font-bold text-sm">{dt.icon}</span>
                    <span className="text-gray-900">{dt.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <label className={`px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            {uploading ? 'Uploading...' : 'Upload'}
            <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
        </div>
      </div>

      <form onSubmit={handleSearch} className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search document contents..."
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none" />
          <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
          </svg>
          {searchQuery && (
            <button type="button" onClick={clearSearch} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">✕</button>
          )}
        </div>
        <button type="submit" disabled={searching} className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50">
          {searching ? 'Searching...' : 'Search'}
        </button>
      </form>

      <div className="bg-white rounded-lg shadow overflow-x-auto"
        onDragOver={e => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragOverId('table'); } }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverId(null); }}
        onDrop={async e => { if (!e.dataTransfer.files.length) return; e.preventDefault(); setDragOverId(null); setUploading(true); for (const file of e.dataTransfer.files) { const formData = new FormData(); formData.append('file', file); if (folderId) formData.append('folderId', folderId); const jwtToken = getCookie('jwtToken'); const tenantId = getCookie('tenantId'); const headers = {}; if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`; if (tenantId) headers['X-Tenant-Id'] = tenantId; await fetch(`${API_BASE}/documents`, { method: 'POST', headers, body: formData }); } setUploading(false); load(); }}>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Size</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {folders.map(folder => (
              <tr key={`f-${folder.id}`} className={`hover:bg-gray-50 ${dragOverId === folder.id ? 'bg-cyan-50 ring-2 ring-inset ring-cyan-400' : ''}`}
                draggable onDragStart={e => onDragStart(e, 'folder', folder.id)}
                onDragOver={e => onDragOver(e, folder.id)} onDragLeave={onDragLeave} onDrop={e => onDrop(e, folder.id)}>
                <td className="px-6 py-4">
                  <button onClick={() => navigateToFolder(folder.id)} className="flex items-center gap-2 text-cyan-600 hover:text-cyan-800 font-medium">
                    <FolderIcon />{folder.name}
                  </button>
                </td>
                <td className="px-6 py-4 text-gray-400 hidden sm:table-cell">Folder</td>
                <td className="px-6 py-4 hidden sm:table-cell">—</td>
                <td className="px-6 py-4 text-gray-600">{new Date(folder.createdAt).toLocaleDateString()}</td>
                <td className="px-6 py-4">
                  <div className="relative">
                    <button onClick={() => setOpenMenu(openMenu === folder.id ? null : folder.id)} className="text-gray-400 hover:text-gray-600">⋯</button>
                    {openMenu === folder.id && (
                      <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border z-10">
                        <button onClick={() => { setOpenMenu(null); openFolderModal(folder); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Rename</button>
                        <button onClick={() => { setOpenMenu(null); setDeleteFolder(folder); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-red-600">Delete</button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {documents.map(doc => (
              <tr key={doc.id} className="hover:bg-gray-50" draggable onDragStart={e => onDragStart(e, 'doc', doc.id)}>
                <td className="px-6 py-4">
                  <Link to={`/documents/${doc.id}`} className="text-cyan-600 hover:text-cyan-800 font-medium">{doc.fileName}</Link>
                </td>
                <td className="px-6 py-4 hidden sm:table-cell"><FileIcon contentType={doc.contentType} /></td>
                <td className="px-6 py-4 text-gray-600 hidden sm:table-cell">{formatSize(doc.size)}</td>
                <td className="px-6 py-4 text-gray-600">{new Date(doc.createdAt).toLocaleDateString()}</td>
                <td className="px-6 py-4">
                  <button onClick={async () => { const r = await api(`/documents/${doc.id}/download`); if (r.ok) { const d = await r.json(); const a = document.createElement('a'); a.href = d.url; a.download = doc.fileName; a.click(); } }}
                    className="text-gray-400 hover:text-cyan-600" title="Download">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {folders.length === 0 && documents.length === 0 && <div className="text-center py-12 text-gray-500">No documents found.</div>}
        {dragOverId === 'table' && (
          <div className="border-2 border-dashed border-cyan-400 bg-cyan-50 text-cyan-600 text-center py-8 mx-4 mb-4 rounded-lg pointer-events-none">
            Drop files here to upload
          </div>
        )}
      </div>

      {createType && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setCreateType(null)}>
          <form onSubmit={handleCreate} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              New {createType === 'docx' ? 'Document' : createType === 'xlsx' ? 'Spreadsheet' : 'Presentation'}
            </h3>
            <input autoFocus type="text" value={createName} onChange={e => setCreateName(e.target.value)}
              placeholder="Enter name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none" />
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setCreateType(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!createName.trim()} className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50">Create</button>
            </div>
          </form>
        </div>
      )}

      {deleteFolder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleteFolder(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Folder</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete "{deleteFolder.name}"? Documents inside will be moved to the root.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteFolder(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDeleteFolder} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {pendingMove && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setPendingMove(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Move {pendingMove.type === 'doc' ? 'Document' : 'Folder'}</h3>
            <p className="text-gray-600 mb-6">Move "{pendingMove.itemName}" to {pendingMove.targetName}?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setPendingMove(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={confirmMove} className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">Move</button>
            </div>
          </div>
        </div>
      )}

      {folderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setFolderModal(null)}>
          <form onSubmit={handleFolderSubmit} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {folderModal === 'new' ? 'New Folder' : 'Rename Folder'}
            </h3>
            <input autoFocus type="text" value={folderName} onChange={e => setFolderName(e.target.value)}
              placeholder="Folder name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none" />
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setFolderModal(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!folderName.trim()} className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50">
                {folderModal === 'new' ? 'Create' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
